<script lang="ts" setup>
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import globals from "../../../globals";
import userService from "../../../services/user.service";
import { parseIp } from "../../../shared/utils/validateIp";
import { debounce } from "@zxcvbn-ts/core";

const { t } = useI18n();

const props = defineProps<{
  knocId: string | null;
  showCancel?: boolean;
}>();

const emit = defineEmits<{
  (e: "success", message: string): void;
  (e: "error", message: string): void;
  (e: "close"): void;
}>();

const ipAddress = ref("");
const validationError = ref("");
const isSubmitting = ref(false);

// Create a debounced version of handleSubmit
function runSubmit() {
  if (isSubmitting.value) return; // Prevent multiple submissions

  isSubmitting.value = true;
  debouncedSubmit();
}

const debouncedSubmit = debounce(async function () {
  try {
    await handleSubmit();
  } finally {
    isSubmitting.value = false;
  }
}, 300);

async function handleSubmit() {
  validationError.value = "";

  if (!ipAddress.value.trim()) {
    validationError.value = t("pages.knocs.dialogs.testGrant.errors.required");
    return;
  }

  const parsed = parseIp(ipAddress.value);

  if (!parsed) {
    validationError.value = t("pages.knocs.dialogs.testGrant.errors.invalid");
    return;
  }

  if (!props.knocId) {
    validationError.value = "No Knoc selected";
    return;
  }

  try {
    await userService.addTestGrant(props.knocId, { ip: parsed });
    const msg = t("pages.knocs.dialogs.testGrant.success");
    emit("success", msg);

    // optionally reset after success
    ipAddress.value = "";
  } catch (error) {
    const errorMessage = globals.renderError(t, error);
    validationError.value = errorMessage;
    emit("error", errorMessage);
  }
}

function handleCancel() {
  emit("close");
}
</script>

<template>
  <form class="space-y-4" @submit.prevent="runSubmit">
    <div>
      <label class="mb-2 block text-sm font-medium text-foreground" for="test-grant-ip">
        {{ t("pages.knocs.dialogs.testGrant.ipLabel") }}
      </label>
      <input
        id="test-grant-ip"
        v-model="ipAddress"
        :class="{ 'border-red-500': validationError }"
        class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        placeholder="192.168.1.1"
        type="text"
        @input="validationError = ''"
        @keyup.enter="runSubmit"
      />
      <p v-if="validationError" class="mt-2 text-sm text-red-500">
        {{ validationError }}
      </p>
      <p class="mt-2 text-sm text-muted-foreground">
        {{ t("pages.knocs.dialogs.testGrant.hint") }}
      </p>
    </div>

    <div class="flex justify-end space-x-2">
      <button
        v-if="showCancel"
        class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
        type="button"
        @click="handleCancel"
      >
        {{ t("common.cancel") }}
      </button>
      <button
        :disabled="isSubmitting"
        class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
        type="submit"
        @click="!isSubmitting && runSubmit()"
      >
        <span
          v-if="isSubmitting"
          class="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
        ></span>
        {{ isSubmitting ? t("common.submitting") : t("common.submit") }}
      </button>
    </div>
  </form>
</template>
